import cx from "classnames";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { FormFieldStates, InputProps } from "@app/shared/qes-standards/forms/exports";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { CSSProperties, FC, PropsWithChildren, RefObject, SelectHTMLAttributes, useMemo, useState } from "react";

export type QESSelectProps = InputProps & SelectHTMLAttributes<HTMLSelectElement> & {
  /**
   * Allows the field to be empty, i.e. does not disable/hide the very top option.
   */
  allowEmpty?: boolean;

  hideDropdownIcon?: boolean;

  /**
   * Expands the select control to display all of the options and allows users to choose multiple options at a time
   * Disabled by default
   */
  multiple?: boolean;

  /**
   * Adds a disabled initial option to the top of the list of options
   */
  placeholder?: string;

  positionDescriptionTextAbsolutely?: boolean;
}

/**
 * A standard select/dropdown for easy reuse. It accepts all the same props as a native HTML select element.
 */
/* We call out the custom props here individually, so ...props is just the normal HTML input attributes */
export const QESSelect: FC<PropsWithChildren<QESSelectProps>> = (props) => {
  const {
    allowEmpty,
    containerClassName,
    descriptiveText,
    errorText,
    hideDropdownIcon,
    hideLabel,
    inlineLabel,
    isValid,
    label,
    labelWidth,
    multiple,
    placeholder,
    qesRef,
    required,
    testId,
    unrequiredValidation,
    wasFocusedExternally,
    validationTooltipContent,
    validationTooltipPosition,
    positionDescriptionTextAbsolutely,
    ...otherProps
  } = props;
  const [hasBeenFocused, setHasBeenFocused] = useState(wasFocusedExternally);

  const fieldState = useMemo(() => {
    if (!isValid && (required || unrequiredValidation) && (hasBeenFocused || wasFocusedExternally)) {
      return FormFieldStates.Error;
    }
    return FormFieldStates.None;
  }, [isValid, required, unrequiredValidation, hasBeenFocused, wasFocusedExternally]);

  const subText = useMemo(() => {
    if ((hasBeenFocused || wasFocusedExternally) && errorText && fieldState === FormFieldStates.Error) {
      return errorText;
    } else if (descriptiveText) {
      return descriptiveText;
    }
    return "";
  }, [descriptiveText, errorText, fieldState, hasBeenFocused, wasFocusedExternally]);

  const descriptionStyle: CSSProperties = {};
  if (positionDescriptionTextAbsolutely === true)
    descriptionStyle.position = "absolute";

  const isShowingError = (hasBeenFocused || wasFocusedExternally) && fieldState === FormFieldStates.Error;

  return (
    <div className={containerClassName || ""} key={testId} onBlur={() => setHasBeenFocused(true)}>
      <div
        className={cx({
          "inline-label": inlineLabel,
          "qes-select__container": true,
        })}
      >
        <label
          htmlFor={props.id || testId}
          className={cx({
            "qes-select__label": true,
            "visually-hidden-label": hideLabel,
          })}
          style={{ width: labelWidth ?? "auto" }}
        >
          {label}
          {required || validationTooltipContent ? (
            <span className="qes-select__label-subcontent">
              {required ? <span className="text-red500"> *</span> : ""}
              {validationTooltipContent ? <TooltipWrapper containerClassName="qes-select__tooltip-container" text={validationTooltipContent} position={validationTooltipPosition}><QesIcon iconName={QesIcons.InfoCircle} description="Info" width="16" /></TooltipWrapper> : ""}
            </span>
          ) : null}
        </label>
        <div className="qes-select__subcontainer">
          <select
            {...otherProps}
            id={otherProps.id || testId}
            className={cx({
              "error": isShowingError,
              "qes-select__select": true,
              [otherProps.className]: !!otherProps.className,
            })}
            data-testid={testId}
            required={required}
            multiple={!!multiple}
            ref={qesRef as RefObject<HTMLSelectElement>}
          >
            <option value="" disabled={!allowEmpty} hidden={!allowEmpty}>{placeholder || ""}</option>
            {otherProps.children}
          </select>
          {hideDropdownIcon ? null : <QesIcon className="qes-select-icon" iconName={QesIcons.ChevronDown} description="Expand" width="16" />}
          <div>
            {subText && (
              <small className="qes-select__descriptive-text" style={descriptionStyle}>
                {isShowingError && <QesIcon iconName={QesIcons.XCircle} isDecorative />}
                {subText}
              </small>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
